import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon, 
  LockClosedIcon, KeyIcon 
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const { get, del } = useApi();
  const { user: currentUser, loading: authLoading } = useAuth(); // Get authLoading
  const navigate = useNavigate();

  // Debug logs
  console.log('=== Users Component ===');
  console.log('Auth loading:', authLoading);
  console.log('Current user:', currentUser);
  console.log('User roles:', currentUser?.roles);
  console.log('Is admin?', currentUser?.roles?.includes('ADMIN'));

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    // Only fetch users when auth is done loading
    if (!authLoading) {
      fetchUsers();
    }
  }, [authLoading]); // Re-run when authLoading changes

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    
    try {
      const { data, error } = await get('/api/users');
      
      if (error) {
        console.error('Error fetching users:', error);
        if (error.includes('403') || error.includes('forbidden')) {
          setPermissionDenied(true);
        } else {
          setError(error);
        }
        setUsers([]);
      } else if (data) {
        console.log('Users received:', data);
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Exception in fetchUsers:', err);
      if (err.message?.includes('403') || err.response?.status === 403) {
        setPermissionDenied(true);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const { error } = await del(`/api/users/${id}`);
      if (!error) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        toast.error(error || 'Failed to delete user');
      }
    }
  };

  const columns = [
    { header: 'User ID', accessor: 'userId' },
    { header: 'Username', accessor: 'username' },
    { header: 'Full Name', accessor: 'fullName' },
    { header: 'Email', accessor: 'email' },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      header: 'Actions', 
      accessor: (row) => {
        // Check if currentUser exists and is admin before showing admin buttons
        const showAdminButtons = currentUser && isAdmin;
        
        return (
          <div className="flex space-x-2">
            {/* View button - always visible */}
            <button
              onClick={() => navigate(`/users/${row.userId}`)}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              title="View"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
            
            {/* Admin buttons - only visible to admin */}
            {showAdminButtons && (
              <>
                <button
                  onClick={() => navigate(`/users/edit/${row.userId}`)}
                  className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                  title="Edit"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate(`/users/${row.userId}/reset-password`)}
                  className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                  title="Reset Password"
                >
                  <KeyIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDelete(row.userId)}
                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        );
      }
    }
  ];

  // Show loading while auth is loading OR users are loading
  if (authLoading || loading) {
    return <LoadingSpinner />;
  }
  
  if (permissionDenied) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-8 text-center">
          <LockClosedIcon className="mx-auto h-12 w-12 text-yellow-500 dark:text-yellow-400" />
          <h2 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Access Denied</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You don't have permission to view users. 
            Please contact your administrator.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users</h1>
        
        {/* Add User button - only visible to admin */}
        {currentUser && isAdmin && (
          <button
            onClick={() => navigate('/users/add')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add User</span>
          </button>
        )}
      </div>

      {users.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No users found</p>
        </div>
      ) : (
        <DataTable columns={columns} data={users} />
      )}
    </div>
  );
};

export default Users;